/**
 * Logistik und Zoll AM PRODUKTMODELL (0057).
 *
 * WARUM ES DAS GIBT.
 *
 * Die vier Ausfuehrungen von HB200-U teilen Tarifnummer, Verpackung und
 * Masterkarton. Bis hierher standen diese Angaben nur an der SKU -- also
 * viermal, und wer eine korrigierte, korrigierte eine von vier. Hier stehen
 * sie einmal; jede SKU ohne eigene Angabe erbt sie, und eine SKU mit eigener
 * Angabe behaelt diese.
 *
 * WAS HIER BEWUSST FEHLT.
 *
 * Kein Uebertragen von SKU-Werten auf das Modell. Dass vier SKUs dieselbe
 * Zahl tragen, ist ein Hinweis, kein Beweis (PRODUCT_DATA_MODEL.md 20.21) --
 * ob sie fuer das Modell gilt, entscheidet ein Mensch, und er traegt sie hier
 * ein.
 *
 * Ein Stand wird nicht Ebene fuer Ebene umgebaut, sondern als neue Revision
 * angelegt -- dasselbe Formular wie an der Lieferbeziehung. Die Revision ist
 * die Geschichte; sie ueberschreibt nichts.
 */
import { useCallback, useEffect, useState } from "react";
import type { MetaResponse, ModelPackagingResponse } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { LogistikRaster, VersionForm, type Produktmasse } from "./ArticleLogistics.tsx";
import "./ArticleLogistics.css";

export function ModelLogistics({ modelId, meta, mayWrite }: {
  modelId: number;
  meta: MetaResponse;
  mayWrite: boolean;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [data, setData] = useState<ModelPackagingResponse | null>(null);
  const [masse, setMasse] = useState<Produktmasse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tarifOffen, setTarifOffen] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  const [neuerStand, setNeuerStand] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setData(await api.modelLogistics(modelId)); }
    catch (e) { setError(e); }
    /* Die Produktmasse des Modells sind MERKMALE des Modells -- dieselbe
       Quelle, aus der die SKUs sie erben. */
    try {
      const r = await api.modelProperties(modelId);
      const zahl = (code: string): string | null =>
        r.items.find((i) => i.definition === code)?.num ?? null;
      setMasse({
        laenge: zahl("length"), durchmesser: zahl("diameter"),
        breite: zahl("width"), hoehe: zahl("height"), gewicht: zahl("weight"),
      });
    } catch { setMasse(null); }
  }, [modelId]);

  useEffect(() => { setData(null); setTarifOffen(false); setNeuerStand(false); void load(); }, [load]);

  async function tarifSpeichern(): Promise<void> {
    try {
      setData(await api.setModelTariffCode(modelId,
        { tariffCode: entwurf.trim() === "" ? null : entwurf.trim() }));
      setTarifOffen(false);
    } catch (e) { setError(e); }
  }

  const erste = (data?.versions.length ?? 0) === 0;

  return (
    <section className="models__block model-log">
      <h3 className="detail__section">{t("models.logistics")}</h3>
      <p className="models__note">{t("models.logisticsNote")}</p>
      {error !== null && <p className="log__error">{errorText(i18n, error)}</p>}
      {data === null && error === null && <p className="log__note">{t("common.loading")}</p>}

      {data !== null && (
        <>
          <LogistikRaster masse={masse} staende={[]} amModell
            modellStaende={{ model: data.model, versions: data.versions }}
            geladen tariffCode={data.tariffCode} tariffErbe={null}
            dutyRate={data.dutyRate} i18n={i18n} />

          {mayWrite && (
            <div className="model-log__aktionen">
              {!tarifOffen && (
                <button className="btn btn--small"
                  onClick={() => { setEntwurf(data.tariffCode ?? ""); setTarifOffen(true); }}>
                  {t("models.editTariff")}
                </button>
              )}
              {!neuerStand && (
                <button className="btn btn--small" onClick={() => setNeuerStand(true)}>
                  {t("log.addVersion")}
                </button>
              )}
            </div>
          )}

          {tarifOffen && mayWrite && (
            <div className="log__form">
              <input className="list__search" value={entwurf}
                onChange={(e) => setEntwurf(e.target.value)}
                aria-label={t("log.tariffCode")} placeholder={t("log.tariffPlaceholder")} />
              <button className="btn btn--primary" onClick={() => void tarifSpeichern()}>
                {t("common.save")}
              </button>
              <button className="btn" onClick={() => setTarifOffen(false)}>{t("common.cancel")}</button>
              <p className="log__note">{t("models.tariffHint")}</p>
            </div>
          )}

          {neuerStand && mayWrite && (
            <VersionForm erste={erste} meta={meta}
              anlegen={(input) => api.createModelPackagingVersion(modelId, input)}
              onDone={(r) => { setNeuerStand(false); if (r) setData(r); }}
              onError={setError} />
          )}
        </>
      )}
    </section>
  );
}
